import { Editor, Element, Node, Path, Range, Text, Transforms } from 'slate';
import { elements, gridBlocks, headingBlocks, innerBlocks } from '../elements';
import { withHistory } from 'slate-history';
import cloneElement from '@Utils/cloneElement';
import isUrl from 'is-url';
import { merge, transform } from 'lodash';

const isLinkActive = editor => {
    const [link] = Editor.nodes(editor, {
        match: n =>
            !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
    });
    return !!link;
};

const unwrapLink = (editor, split) => {
    Transforms.unwrapNodes(editor, {
        match: n =>
            !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
        split,
    });
};

const getLink = editor => {
    return Editor.above(editor, {
        match: n =>
            !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
    });
};

const wrapLinkXX = (editor, url: string) => {
    if (isLinkActive(editor)) {
        unwrapLink(editor);
    }

    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);
    const link = {
        type: 'link',
        attributes: {
            href: url,
            target: '_blank',
        },
        children: isCollapsed ? [{ text: url }] : [],
    };

    if (isCollapsed) {
        Transforms.insertNodes(editor, link);
    } else {
        Transforms.wrapNodes(editor, link, { split: true });
        Transforms.collapse(editor, { edge: 'end' });
    }
};

const isRichTextDescendant = editor => {
    return !!Editor.above(editor, {
        match: n => Element.isElement(n) && n.type === 'rich-text',
    });
};

const wrapLink = (editor, url: string, text) => {
    unwrapLink(editor, true);
    Transforms.insertNodes(editor, {
        type: 'link',
        attributes: { href: url, target: '_blank' },
        children: [{ text }],
    });
    // Transforms.wrapNodes(editor, {
    //     type: 'link',
    //     attributes: { href: url, target: '_blank' },
    //     children: [],
    // });
};

const withEmailEditor = (editor: Editor & ReturnType<typeof withHistory>) => {
    const {
        isVoid,
        isInline,
        insertBreak,
        deleteBackward,
        deleteForward,
        insertText,
        normalizeNode,
        markableVoid,
    } = editor;

    // editor.insertSoftBreak = () => Editor.insertText(editor, '\n');
    editor.insertSoftBreak = () => {
        Transforms.insertNodes(editor, cloneElement('soft-break'), {
            select: true,
        });
        Transforms.move(editor, { unit: 'character' });
    };

    editor.insertBreak = () => {
        const isAboveRichText = Editor.above(editor, {
            match: n => Element.isElement(n) && n.type === 'rich-text',
        });

        const heading = Editor.above(editor, {
            match: n => Element.isElement(n) && headingBlocks.includes(n.type),
        });

        // check if the selection is at the end of the block
        if (heading) {
            console.log(editor.selection);

            // const isEnd =
            //     editor.selection.anchor.offset ===
            //     Node.string(heading[0]).length;
            // console.log(isEnd);

            const isEnd = Editor.isEnd(
                editor,
                editor.selection.anchor,
                heading[1],
            );

            if (isEnd) {
                Transforms.insertNodes(editor, cloneElement('paragraph'));
                return;
            }
        }

        if (isAboveRichText) insertBreak();
    };

    editor.insertText = text => {
        if (text && isRichTextDescendant(editor) && isUrl(text))
            wrapLinkXX(editor, text);
        else insertText(text);
    };

    editor.deleteBackward = unit => {
        const { selection } = editor;

        if (selection && Range.isCollapsed(selection)) {
            const [start] = Range.edges(selection);

            const [, elementPath] = Editor.above(editor, {
                match: n =>
                    Element.isElement(n) && innerBlocks.includes(n.type),
            });

            const range = Editor.range(editor, elementPath, start);

            const beforeString = Editor.string(editor, range);
            if (!beforeString.length) return;
        }
        deleteBackward(unit);
    };

    editor.deleteForward = unit => {
        const { selection } = editor;
        if (selection && Range.isCollapsed(selection)) {
            const [start] = Range.edges(selection);

            const [, elementPath] = Editor.above(editor, {
                match: n =>
                    Element.isElement(n) && innerBlocks.includes(n.type),
            });

            const endPoint = Editor.end(editor, elementPath);

            const range = Editor.range(editor, start, endPoint);

            const afterString = Editor.string(editor, range);

            if (!afterString.length) return;
        }
        deleteForward(unit);
    };

    editor.isVoid = element => elements[element.type]?.void || isVoid(element);

    editor.markableVoid = element =>
        ['merge-tag', 'soft-break'].includes(element.type) ||
        markableVoid(element);

    editor.isInline = element =>
        elements[element.type]?.inline || isInline(element);

    editor.normalizeNode = entry => {
        const [node, path] = entry;
        const { type, children } = node;

        const childrenLength = children?.length;

        // if (Text.isText(node)) {
        //     console.log('Text', node);
        //     const parentFontSize = Node.parent(editor, path)?.attributes?.style
        //         ?.fontSize;
        //     if (parentFontSize === node?.fontSize) {
        //         editor.removeMark('fontSize');
        //         return;
        //     }
        // }

        if (
            Element.isElement(node) &&
            [...headingBlocks, 'paragraph'].includes(type)
        ) {
            const texts = Array.from(Node.texts(node)).filter(
                ([text]) => text.text !== '',
            );
            if (!texts.length) return;

            const allHaveFontSize = texts.every(([obj]) =>
                obj.hasOwnProperty('fontSize'),
            );
            if (allHaveFontSize) {
                const largestFontSize = texts.reduce(
                    (acc, [{ fontSize }]) =>
                        parseInt(fontSize) > acc ? parseInt(fontSize) : acc,
                    0,
                );

                const mergedAttributes = merge({}, node.attributes, {
                    style: { fontSize: `${largestFontSize}px` },
                });
                Transforms.setNodes(
                    editor,
                    { fontSize: undefined },
                    {
                        at: path,
                        mode: 'all',
                        match: n =>
                            Text.isText(n) &&
                            n.fontSize === `${largestFontSize}px`,
                    },
                );
                Transforms.setNodes(
                    editor,
                    { attributes: mergedAttributes },
                    { at: path },
                );
                return;
            }
        }

        if (childrenLength === 0) {
            if (gridBlocks.includes(type)) {
                Transforms.removeNodes(editor, { at: path });
                return;
            } else if (type === 'section') {
                Transforms.insertNodes(editor, cloneElement('placeholder'), {
                    at: path.concat(0),
                });
                return;
            } else if (type === 'container') {
                Transforms.insertNodes(editor, cloneElement('section'), {
                    at: path.concat(0),
                });
                return;
            }
        } else if (childrenLength === 1) {
            if (type === 'row') {
                Transforms.unwrapNodes(editor, { at: path });
                Transforms.unwrapNodes(editor, { at: path });
            }
            return;
        } else if (childrenLength > 1) {
            if (['section', 'column'].includes(type)) {
                for (const [child, childPath] of Node.children(editor, path)) {
                    const nextChild = Editor.next(editor, { at: childPath });
                    if (!nextChild) return;
                    const [nextChildNode, nextChildPath] = nextChild;
                    if (
                        child.type === 'column' &&
                        nextChildNode.type === 'column'
                    ) {
                        const range = {
                            anchor: { path: childPath, offset: 0 },
                            focus: { path: nextChildPath, offset: 0 },
                        };
                        Transforms.wrapNodes(editor, elements.row.node, {
                            at: range,
                        });
                        return;
                    }
                }
                return;
            }
            if (type === 'row') {
                const lastChild = children[childrenLength - 1];
                if (lastChild.attributes.style?.width) {
                    const attributes = structuredClone(lastChild.attributes);
                    delete attributes.style.width;
                    Transforms.setNodes(
                        editor,
                        { attributes },
                        { at: [...path, childrenLength - 1] },
                    );
                    return;
                }
            }
        }
        normalizeNode(entry);
    };

    editor.unwrapLink = () => unwrapLink(editor);

    editor.wrapLink = (url, text) => wrapLink(editor, url, text);

    editor.getLink = () => getLink(editor);

    return editor;
};

export default withEmailEditor;
